import type { JSX, ReactNode } from "react";
import type { PendingPrompt } from "@magnis/client-core";

export interface PendingPromptPanelProps {
  /** Full queue of pending prompts (head shown, rest counted). */
  readonly queue: readonly PendingPrompt[];
  /** Renderer delegated to the existing AgentPanel render pipeline. */
  readonly renderPrompt: (prompt: PendingPrompt) => ReactNode;
}

/**
 * Pinned panel rendered above the docked composer. Shows the head of the
 * pending-prompt queue and a subtle `N of M` counter when more prompts are
 * queued behind it. Rendering delegates to the caller so module-specific
 * renderers (ContactCreate, EmailBatchSend, etc.) keep their full UI.
 *
 * Returns null when the queue is empty so the composer sits flush against
 * the feed.
 */
export function PendingPromptPanel({
  queue,
  renderPrompt,
}: PendingPromptPanelProps): JSX.Element | null {
  const head = queue[0];
  if (!head) return null;
  const total = queue.length;

  return (
    <div
      data-testid="pending-prompt-panel"
      data-prompt-kind={head.kind}
      data-prompt-id={head.toolCall.id}
      data-queue-total={String(total)}
      className="shrink-0 px-4 pt-1.5 pb-1"
    >
      {total > 1 && (
        <div
          data-testid="pending-prompt-panel-counter"
          className="mb-2 text-[11px] font-medium text-agent-text-dim"
        >
          {`1 of ${String(total)}`}
        </div>
      )}
      <div>{renderPrompt(head)}</div>
    </div>
  );
}
